// Email verification service for headmaster login using SendGrid
export interface VerificationData {
  email: string;
  code: string;
  expiresAt: number;
}

class EmailVerificationService {
  private verificationCodes = new Map<string, VerificationData>();
  private readonly SENDER_EMAIL = 'noreply@moe.gov.gy';
  private readonly CODE_EXPIRY_MINUTES = 10;

  // Generate a 6-digit verification code
  private generateVerificationCode(): string {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  // Send verification email using Supabase Edge Function with SendGrid
  async sendVerificationCode(userEmail: string, userName?: string): Promise<string> {
    try {
      const code = this.generateVerificationCode();
      const expiresAt = Date.now() + (this.CODE_EXPIRY_MINUTES * 60 * 1000);
      
      // Store the verification code
      this.verificationCodes.set(userEmail, {
        email: userEmail,
        code,
        expiresAt
      });

      // Get Supabase configuration
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      // console.log('🔍 Checking Supabase configuration...');
      // console.log('Supabase URL exists:', !!supabaseUrl);
      // console.log('Supabase Anon Key exists:', !!supabaseAnonKey);
      
      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration not found');
      }

      try {
        // console.log('📧 Attempting to send verification email via Supabase Edge Function...');
        // console.log('Email:', userEmail);
        // console.log('Code:', code);
        
        // Call Supabase Edge Function to send email
        const response = await fetch(`${supabaseUrl}/functions/v1/send-verification-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({
            email: userEmail,
            code: code,
            userName: userName
          })
        });

        if (response.ok) {
          await response.json();
          // console.log('✅ Verification email sent successfully via SendGrid!');
          // console.log('📧 From: noreply@moe.gov.gy');
          // console.log('📧 To:', userEmail);
          return code;
        } else {
          const errorData = await response.json();
          console.error('❌ Supabase function error:', errorData);
          throw new Error(`Email service error: ${errorData.error || 'Unknown error'}`);
        }
        
        } catch (fetchError) {
        console.warn('⚠️ Supabase function call failed, showing code for development:', fetchError);
        
        // Show the code so the user can still proceed
        // console.log('🔢 Generated verification code:', code);
        alert(`Email sending failed, but your verification code is: ${code}\n\nExpires in ${this.CODE_EXPIRY_MINUTES} minutes.\n\nError: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}\n\nNote: In production, this would be sent from noreply@moe.gov.gy`);
        
        return code;
      }    } catch (error) {
      console.error('❌ Error in email verification service:', error);
      
      // Return the code anyway so the user can still proceed
      const storedData = this.verificationCodes.get(userEmail);
      if (storedData) {
        // console.log('⚠️ Network error occurred, but verification code was generated:', storedData.code);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        alert(`Network error occurred, but your verification code is: ${storedData.code}\n\nExpires in ${this.CODE_EXPIRY_MINUTES} minutes.\n\nError: ${errorMessage}`);
        return storedData.code;
      }
      
      throw new Error('Failed to generate verification code. Please try again.');
    }
  }

  // Verify the entered code
  verifyCode(userEmail: string, enteredCode: string): boolean {
    const storedData = this.verificationCodes.get(userEmail);
    
    if (!storedData) {
      return false; // No code found
    }
    
    if (Date.now() > storedData.expiresAt) {
      this.verificationCodes.delete(userEmail); // Clean up expired code
      return false; // Code expired
    }
    
    if (storedData.code === enteredCode) {
      this.verificationCodes.delete(userEmail); // Clean up used code
      return true; // Code is valid
    }
    
    return false; // Code doesn't match
  }

  // Check if a verification code exists and is still valid
  hasValidCode(userEmail: string): boolean {
    const storedData = this.verificationCodes.get(userEmail);
    
    if (!storedData) {
      return false;
    }
    
    if (Date.now() > storedData.expiresAt) {
      this.verificationCodes.delete(userEmail);
      return false;
    }
    
    return true;
  }

  // Get remaining time for code expiry (in minutes)
  getRemainingTime(userEmail: string): number {
    const storedData = this.verificationCodes.get(userEmail);
    
    if (!storedData) {
      return 0;
    }
    
    const remainingMs = storedData.expiresAt - Date.now();
    return Math.max(0, Math.ceil(remainingMs / (60 * 1000)));
  }

  // Clear verification code (useful for cleanup)
  clearCode(userEmail: string): void {
    this.verificationCodes.delete(userEmail);
  }

  // Send password reset verification code
  async sendPasswordResetCode(userEmail: string, userName?: string): Promise<string> {
    try {
      const code = this.generateVerificationCode();
      const expiresAt = Date.now() + (this.CODE_EXPIRY_MINUTES * 60 * 1000);
      
      // Store the verification code with a special prefix to differentiate from regular verification
      const resetKey = `reset_${userEmail}`;
      this.verificationCodes.set(resetKey, {
        email: userEmail,
        code,
        expiresAt
      });

      // Get Supabase configuration
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      console.log('🔍 Checking Supabase configuration for password reset...');
      console.log('Supabase URL exists:', !!supabaseUrl);
      console.log('Supabase Anon Key exists:', !!supabaseAnonKey);
      
      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration not found');
      }

      try {
        console.log('📧 Attempting to send password reset code via Supabase Edge Function...');
        console.log('Email:', userEmail);
        console.log('Code:', code);
        
        // Call Supabase Edge Function to send password reset email
        const response = await fetch(`${supabaseUrl}/functions/v1/send-verification-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({
            email: userEmail,
            code: code,
            userName: userName,
            type: 'password_reset'
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('✅ Password reset code sent successfully!');
          console.log('📧 From: noreply@moe.gov.gy');
          console.log('📧 To:', userEmail);
          console.log('Result:', result);
          return code;
        } else {
          const errorData = await response.json();
          console.error('❌ Supabase function error:', errorData);
          throw new Error(`Password reset service error: ${errorData.error || 'Unknown error'}`);
        }
        
      } catch (fetchError) {
        console.warn('⚠️ Supabase function call failed, showing code for development:', fetchError);
        
        // Show the code so the user can still proceed
        console.log('🔢 Generated password reset code:', code);
        alert(`Password reset email failed, but your verification code is: ${code}\n\nExpires in ${this.CODE_EXPIRY_MINUTES} minutes.\n\nError: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}\n\nNote: In production, this would be sent from noreply@moe.gov.gy`);
        
        return code;
      }
      
    } catch (error) {
      console.error('❌ Error in password reset service:', error);
      
      // Return the code anyway so the user can still proceed
      const resetKey = `reset_${userEmail}`;
      const storedData = this.verificationCodes.get(resetKey);
      if (storedData) {
        console.log('⚠️ Network error occurred, but password reset code was generated:', storedData.code);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        alert(`Network error occurred, but your password reset code is: ${storedData.code}\n\nExpires in ${this.CODE_EXPIRY_MINUTES} minutes.\n\nError: ${errorMessage}`);
        return storedData.code;
      }
      
      throw new Error('Failed to generate password reset code. Please try again.');
    }
  }

  // Verify password reset code
  verifyPasswordResetCode(userEmail: string, enteredCode: string): boolean {
    const resetKey = `reset_${userEmail}`;
    const storedData = this.verificationCodes.get(resetKey);
    
    if (!storedData) {
      return false; // No code found
    }
    
    if (Date.now() > storedData.expiresAt) {
      this.verificationCodes.delete(resetKey); // Clean up expired code
      return false; // Code expired
    }
    
    if (storedData.code === enteredCode) {
      // Don't delete the code yet - we'll delete it after successful password reset
      return true; // Code is valid
    }
    
    return false; // Code doesn't match
  }

  // Clear password reset code after successful password change
  clearPasswordResetCode(userEmail: string): void {
    const resetKey = `reset_${userEmail}`;
    this.verificationCodes.delete(resetKey);
  }

  // Check if a password reset code exists and is still valid
  hasValidPasswordResetCode(userEmail: string): boolean {
    const resetKey = `reset_${userEmail}`;
    const storedData = this.verificationCodes.get(resetKey);
    
    if (!storedData) {
      return false;
    }
    
    if (Date.now() > storedData.expiresAt) {
      this.verificationCodes.delete(resetKey);
      return false;
    }
    
    return true;
  }
}

// Export singleton instance
export const emailVerificationService = new EmailVerificationService();